import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {FreezableFilterExplanationTooltip} from 'components/Accommodation/DealFreezeToast/FreezableFilterExplanationTooltip'
import {scrollToSection} from 'components/ScrollSpy'
import {useHotelRoomsFilters} from 'hooks/useHotelRoomsFilters'
import {getOfferContext} from 'middleware/analytics/selectors'
import {AccommodationPageNavSections} from 'modules/accommodation/types'

import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {Action, Category} from '@vio/core/src/analytics/types/Events'
import {RoomsFilterKey} from '@vio/core/src/room/business/utils/filters'
import {
  DealFreezeBannerVariant,
  trackDealFreezeBanner
} from '@vio/shared/src/dealFreeze/components/common/bannerTracking'
import {
  DealFreezeOfferBoxBanner,
  DealFreezeOfferBoxCard,
  dealFreezeOfferBoxMessages
} from '@vio/shared/src/dealFreeze/components/DealFreezeOfferBox'
import {
  ComponentSource,
  DealFreezeOnboardingSource
} from '@vio/shared/src/dealFreeze/types'
import {readFreezableFilterTooltipShown} from '@vio/shared/src/dealFreeze/utils/FreezableFilterTooltip'

import {DealFreezeOfferBoxState} from './useDealFreezeOfferBox'

interface Props {
  hotelId: string
  sourceComponent: string
  state: DealFreezeOfferBoxState
  variant?: 'banner' | 'card'
  scrollTarget?: AccommodationPageNavSections
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`

export const DealFreezeOfferBoxSlot = ({
  hotelId,
  sourceComponent,
  state,
  variant = 'banner',
  scrollTarget = AccommodationPageNavSections.RoomsFilters
}: Props) => {
  const {fromPrice, scenario} = state
  const {formatMessage} = useIntl()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const offerContext = useSelector(s =>
    state.freezableTopOfferId
      ? getOfferContext(s, hotelId, state.freezableTopOfferId, sourceComponent)
      : null
  )
  const {activeRoomsFilters, toggleRoomsFilter} = useHotelRoomsFilters()
  const [isFilterTooltipOpen, setIsFilterTooltipOpen] = useState(false)
  const {ref, inView} = useInView({threshold: 0.5, triggerOnce: true})

  const track = (category: Category, action: Action) =>
    dispatchTeamEvent(
      trackDealFreezeBanner({
        category,
        action,
        variant: DealFreezeBannerVariant.OfferBox,
        analyticsContext: {offerContext},
        component: ComponentSource.HotelViewTopOffers,
        source: DealFreezeOnboardingSource.OfferBox,
        payload: {scenario, hasFromPrice: Boolean(fromPrice), layout: variant}
      })
    )

  useEffect(() => {
    if (inView) track(Category.System, Action.Displayed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  const handleClick = () => {
    track(Category.User, Action.Clicked)
    if (!activeRoomsFilters.includes(RoomsFilterKey.DealFreeze)) {
      toggleRoomsFilter(RoomsFilterKey.DealFreeze)
    }
    scrollToSection(scrollTarget)
    if (!readFreezableFilterTooltipShown()) setIsFilterTooltipOpen(true)
  }

  return (
    <Wrapper ref={ref}>
      {variant === 'card' ? (
        <DealFreezeOfferBoxCard onClick={handleClick} fromPrice={fromPrice} />
      ) : (
        <DealFreezeOfferBoxBanner onClick={handleClick} fromPrice={fromPrice} />
      )}
      <FreezableFilterExplanationTooltip
        isOpen={isFilterTooltipOpen}
        onClose={() => setIsFilterTooltipOpen(false)}
        content={formatMessage(dealFreezeOfferBoxMessages.filterTooltip)}
      />
    </Wrapper>
  )
}
